import type { NotifiableError } from "@bugsnag/js";

import type {
  BugsnagEventOptions,
  BugsnagMetadata,
} from "@/react/helpers/bugsnagHelpers";
import { notifyBugsnag } from "@/react/helpers/bugsnagHelpers";
import { isDevelopmentEnv } from "@/react/helpers/envHelpers";

export interface ReportCheckoutErrorData {
  caller?: string;
  communityId?: number;
  communityMemberId?: number;
  communityMemberUserId?: number;
  extra: Record<string, unknown>;
  paywallId?: number;
  paywallPriceId?: number;
  sessionId?: number;
  sessionStepId?: number;
  stripeElementsMode?: string;
  stripePaymentMethodType?: string;
}

const toStringOrUnknown = (value: unknown): string =>
  typeof value === "string" ? value : "unknown";

// Produce a stable grouping key so distinct failure types appear as separate
// Bugsnag errors instead of collapsing into one group by minified throw site.
const getGroupingHash = (data: ReportCheckoutErrorData): string | undefined => {
  const { extra } = data;
  const errorType = extra["checkout_error_type"];
  if (
    errorType === "paywallError" &&
    extra["paywall_failure_category"] !== undefined &&
    extra["paywall_failure_code"] !== undefined
  ) {
    const category = toStringOrUnknown(extra["paywall_failure_category"]);
    const code = toStringOrUnknown(extra["paywall_failure_code"]);
    return `paywallError:${category}:${code}`;
  }
  if (errorType === "stripeError") {
    const stripeType = toStringOrUnknown(extra["stripe_error_type"]);
    const stripeCode = toStringOrUnknown(extra["stripe_error_code"]);
    return `stripeError:${stripeType}:${stripeCode}`;
  }
  return undefined;
};

const isExpectedStripeError = (data: ReportCheckoutErrorData): boolean => {
  const { extra } = data;
  if (extra["checkout_error_type"] !== "stripeError") return false;
  const stripeErrorType = extra["stripe_error_type"];

  // Card errors (declines, insufficient funds, expired) are user issues
  if (stripeErrorType === "card_error") return true;

  if (stripeErrorType === "validation_error") {
    const stripeErrorCode = extra["stripe_error_code"];
    // Apple Pay / Google Pay cancellation: user dismisses the wallet sheet,
    // Stripe fires submitElements() and returns validation_error with code "incomplete".
    // incomplete_payment_view / incomplete_address_view: user has an in-progress
    // payment method or address update that hasn't been saved yet -- user action, not a bug.
    return (
      stripeErrorCode === "incomplete" ||
      stripeErrorCode === "invalid_payment_view.update" ||
      stripeErrorCode === "invalid_address_view.update"
    );
  }

  // Network connectivity issues -- not actionable bugs
  if (stripeErrorType === "api_connection_error") return true;

  if (stripeErrorType === "invalid_request_error") {
    const stripeErrorCode = extra["stripe_error_code"];
    // 3DS authentication failures (payment or setup intent) are expected user behavior.
    // Stripe lock_timeout from concurrent requests -- rare, not actionable.
    return (
      stripeErrorCode === "payment_intent_authentication_failure" ||
      stripeErrorCode === "setup_intent_authentication_failure" ||
      stripeErrorCode === "lock_timeout"
    );
  }

  return false;
};

// Specific processor-category codes that are definitively bank/card-issuer
// decisions. These are safe to suppress because the user receives a clear
// error message and the backend has handled the failure correctly.
// NOTE: The generic "generic" code is intentionally excluded -- it can mask
// real Circle bugs and should remain visible in Bugsnag.
const EXPECTED_PROCESSOR_CODES = new Set([
  "authentication_required",
  "payment_authorization_failed",
  "payment_intent_payment_attempt_expired",
  "payment_intent_payment_attempt_failed",
  "declined_insufficient_funds",
  "declined_invalid_card_info",
  "declined_generic",
  "declined_provider",
  "declined_unactivated",
  "amount_too_small",
  "tax_id_invalid",
]);

const EXPECTED_PAYWALL_CODES = new Set([
  "unsupported_payment_method",
  "checkout_session_processing",
  "already_purchased",
  "coupon_not_active",
  "subscription_change_billing_cycle_imminent",
  "subscription_change_pending_invoice",
]);

// NOTE: session_not_active is intentionally NOT suppressed here. It is the
// primary Bugsnag signal that Bug 3b (stuck checkout attempts) is still
// occurring in production. Suppressing it before Slice C ships would blind
// us to the problem. Once Slice C lands and resolves stuck attempts,
// session_not_active errors should disappear naturally — no suppression needed.
const isExpectedPaywallError = (data: ReportCheckoutErrorData): boolean => {
  const { extra } = data;
  if (extra["checkout_error_type"] !== "paywallError") return false;
  // Only new-style paywall errors carry paywall_failure_code; legacy errors use
  // legacy_paywall_error_details and will not match any suppression below.
  const failureCategory = extra["paywall_failure_category"];
  const failureCode = extra["paywall_failure_code"];
  const providerCode = extra["paywall_failure_provider_code"];

  // Specific processor codes are definitively bank/card-issuer decisions.
  // Not a catch-all for the entire processor category (generic code is excluded
  // as it can mask real Circle bugs and should remain visible in Bugsnag).
  // Backend also correctly rejects unsupported payment methods, duplicate
  // purchases, inactive coupon codes, and checkout-session lock retries.
  return (
    (failureCategory === "processor" &&
      failureCode === "payment_method_confirmation_not_completed" &&
      providerCode === "setup_intent_authentication_failure") ||
    (failureCategory === "processor" &&
      typeof failureCode === "string" &&
      EXPECTED_PROCESSOR_CODES.has(failureCode)) ||
    (typeof failureCode === "string" && EXPECTED_PAYWALL_CODES.has(failureCode))
  );
};

export const reportCheckoutError = (
  error: unknown,
  data: ReportCheckoutErrorData,
) => {
  if (isDevelopmentEnv()) {
    console.error(error, data);
  }
  if (isExpectedStripeError(data) || isExpectedPaywallError(data)) {
    return;
  }

  const bugsnagMetadata: BugsnagMetadata = {
    checkout: {
      ...data.extra,
      caller: data.caller,
      checkout_session_id: data.sessionId,
      community_id: data.communityId,
      community_member_id: data.communityMemberId,
      community_member_user_id: data.communityMemberUserId,
      paywall_id: data.paywallId,
      paywall_price_id: data.paywallPriceId,
      session_step_id: data.sessionStepId,
      stripe_elements_mode: data.stripeElementsMode,
      stripe_payment_method_type: data.stripePaymentMethodType,
      url: window.location.href,
    },
  };
  const bugsnagEventOptions: BugsnagEventOptions = {
    groupingHash: getGroupingHash(data),
  };
  notifyBugsnag(error as NotifiableError, bugsnagMetadata, bugsnagEventOptions);
};
